import CalendarOutlined from '@ant-design/icons/CalendarOutlined';
import { Button, Col, DatePicker, Input, InputRef, Row, Space, theme } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone'; // dependent on utc plugin
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useCompanyTimeZone from '../../../hooks/adminPage/useCompanyTimeZone';
import { guessTimeZone } from '../../../utils/helpers';
import CustomDivider from '../../CustomDivider';
import GrispiInputContainer, { GrispiInputContainerProps } from '../GrispiInputContainer';

const { RangePicker } = DatePicker;

const createDayjsObject = (value: number) => dayjs(value);
const isDayjsObject = (value: [dayjs.Dayjs, dayjs.Dayjs]) => {
    if (!value) return false;
    return dayjs.isDayjs(value[0]) && dayjs.isDayjs(value[1]);
};
export enum PresetDateNameMap {
    'today' = 'today',
    'yesterday' = 'yesterday',
    'thisWeek' = 'thisWeek',
    'lastWeek' = 'lastWeek',
    'thisMonth' = 'thisMonth',
    'lastMonth' = 'lastMonth',
    'thisYear' = 'thisYear',
    'lastYear' = 'lastYear'
}

type GrispiDateInputProps = {
    value: string;
    onChange: (value: string) => void;
    containerProps: GrispiInputContainerProps;
    rangePickerProps?: RangePickerProps;
    presetDateRange?: PresetDateNameMap; //when the rangePicker is cleared, the value will be set given presetDateRange.
};

const transformDayjsValue = (value: [dayjs.Dayjs, dayjs.Dayjs]) => {
    if (isDayjsObject(value)) return value[0].valueOf().toString() + '-' + value[1].valueOf().toString();
    return null;
};
const formattedValue = (value: [dayjs.Dayjs, dayjs.Dayjs], index: number) => {
    if (isDayjsObject(value)) return value[index].format('DD/MM/YYYY');
    return null;
};

const transformFormValue = (value: string) => {
    if (!value) return null;
    const rangeValues = value.split('-');
    if (rangeValues.length > 0 && !Number.isNaN(+rangeValues[0]) && !Number.isNaN(+rangeValues[1]))
        return [createDayjsObject(+rangeValues[0]), createDayjsObject(+rangeValues[1])] as [dayjs.Dayjs, dayjs.Dayjs];
};
const isSelected = (value: string, range: [dayjs.Dayjs, dayjs.Dayjs]) => {
    const rangeString = transformDayjsValue(range);
    if (value === rangeString) return true;
};

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(updateLocale);

const GrispiDateRangeInput = ({
    containerProps,
    onChange,
    value,
    rangePickerProps = {},
    presetDateRange
}: GrispiDateInputProps) => {
    const {
        t,
        i18n: { language }
    } = useTranslation('common');
    const {
        token: { paddingSM, zIndexBase }
    } = theme.useToken();
    const inputRef = useRef<InputRef>(null);
    const [selectedDate, setSelectedDate] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(transformFormValue(value));
    const [activeIndex, setActiveIndex] = useState<0 | 1>(0);
    const { data: zone } = useCompanyTimeZone();
    const userTimeZone = zone ?? guessTimeZone();
    useEffect(() => {
        setSelectedDate(transformFormValue(value));
    }, [value]);

    useEffect(() => {
        dayjs.updateLocale(language, {
            week: {
                dow: 1
            }
        });
    }, [language]);

    const [open, setOpen] = useState(false);
    const presetRanges: { name: PresetDateNameMap; value: [dayjs.Dayjs, dayjs.Dayjs] }[] = [
        {
            name: PresetDateNameMap.today,
            value: [dayjs().tz(userTimeZone).startOf('D'), dayjs().tz(userTimeZone).endOf('D')]
        },
        {
            name: PresetDateNameMap.yesterday,
            value: [
                dayjs().tz(userTimeZone).subtract(1, 'days').startOf('day'),
                dayjs().tz(userTimeZone).subtract(1, 'days').endOf('day')
            ]
        },
        {
            name: PresetDateNameMap.thisWeek,
            value: [dayjs().tz(userTimeZone).startOf('week'), dayjs().tz(userTimeZone).endOf('week')]
        },
        {
            name: PresetDateNameMap.lastWeek,
            value: [
                dayjs().tz(userTimeZone).subtract(1, 'weeks').startOf('week'),
                dayjs().tz(userTimeZone).subtract(1, 'weeks').endOf('week')
            ]
        },
        {
            name: PresetDateNameMap.thisMonth,
            value: [dayjs().tz(userTimeZone).startOf('month'), dayjs().tz(userTimeZone).endOf('month')]
        },
        {
            name: PresetDateNameMap.lastMonth,
            value: [
                dayjs().tz(userTimeZone).subtract(1, 'months').startOf('month'),
                dayjs().tz(userTimeZone).subtract(1, 'months').endOf('month')
            ]
        },
        {
            name: PresetDateNameMap.thisYear,
            value: [dayjs().tz(userTimeZone).startOf('year'), dayjs().tz(userTimeZone).endOf('year')]
        },
        {
            name: PresetDateNameMap.lastYear,
            value: [
                dayjs().tz(userTimeZone).subtract(1, 'year').startOf('year'),
                dayjs().tz(userTimeZone).subtract(1, 'year').endOf('year')
            ]
        }
    ];
    const handleOk = (dateValue: [dayjs.Dayjs, dayjs.Dayjs]) => {
        setSelectedDate(dateValue);
        onChange(
            transformDayjsValue(
                dateValue
                    ? dateValue
                    : presetDateRange
                    ? presetRanges.find((range) => range.name === presetDateRange)?.value
                    : dateValue
            )
        );
        setOpen(false);
        setActiveIndex(0);
        inputRef.current.blur();
    };
    const handleCancel = () => {
        setOpen(false);
        setActiveIndex(0);
        setSelectedDate(transformFormValue(value));
        inputRef.current.blur();
    };
    const handleBlur = () => {
        setOpen(false);
        setActiveIndex(0);
        setSelectedDate(transformFormValue(value));
    };
    const handleIndexChange = () => {
        if (activeIndex === 0) setActiveIndex(1);
        else setActiveIndex(0);
    };
    return (
        <GrispiInputContainer {...containerProps}>
            <Row wrap={false}>
                <Input
                    style={{ backgroundColor: 'transparent', position: 'absolute', zIndex: `${zIndexBase + 1}` }}
                    onBlur={() => handleBlur()}
                    onFocus={() => setOpen(true)}
                    ref={inputRef}
                    readOnly
                />
                {open && (
                    <RangePicker
                        key={language}
                        allowClear={false}
                        open={open}
                        onOpenChange={(o) => {
                            if (o === false) return;
                            setOpen(o);
                        }}
                        activePickerIndex={activeIndex}
                        format="DD/MM/YY"
                        inputReadOnly
                        defaultValue={selectedDate}
                        onChange={(v: [dayjs.Dayjs, dayjs.Dayjs]) => {
                            if (v) {
                                const newV: [dayjs.Dayjs, dayjs.Dayjs] = [v[0]?.startOf('D'), v[1]?.endOf('D')];
                                setSelectedDate(
                                    v ? newV : presetRanges.find((range) => range.name === presetDateRange)?.value
                                );
                            }
                        }}
                        value={selectedDate}
                        onCalendarChange={() => {
                            handleIndexChange();
                        }}
                        renderExtraFooter={() => (
                            <Space
                                direction="vertical"
                                className=" full-width"
                                style={{ padding: `${paddingSM / 2}px ${paddingSM}px` }}
                            >
                                <Row wrap justify="space-around">
                                    {presetRanges.map((range) => {
                                        return (
                                            <Col key={range.name}>
                                                <Button
                                                    size="small"
                                                    type={
                                                        isSelected(transformDayjsValue(selectedDate), range.value)
                                                            ? 'primary'
                                                            : 'default'
                                                    }
                                                    onClick={() => {
                                                        handleOk(range.value);
                                                    }}
                                                >
                                                    {t(`rangeButtons.${range.name}` as any)}
                                                </Button>
                                            </Col>
                                        );
                                    })}
                                </Row>
                                <CustomDivider zeroMargin />
                                <Row justify="end" style={{ gap: `${paddingSM / 2}px` }}>
                                    <Button
                                        type={'primary'}
                                        onClick={() => {
                                            handleOk(selectedDate);
                                        }}
                                        disabled={value === transformDayjsValue(selectedDate)}
                                    >
                                        {t('rangePickerOkButtonText')}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            handleCancel();
                                        }}
                                    >
                                        {t('rangePickerCancelButtonText')}
                                    </Button>
                                </Row>
                            </Space>
                        )}
                        style={{ visibility: 'hidden', width: 0, position: 'absolute' }}
                        {...rangePickerProps}
                    />
                )}

                <Col span={10}>
                    <Input
                        style={{ textAlign: 'center', borderColor: 'transparent' }}
                        value={formattedValue(selectedDate, 0)}
                        readOnly
                    />
                </Col>
                <Col span={1} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    ⇀
                </Col>
                <Col span={10}>
                    <Input
                        style={{ textAlign: 'center', borderColor: 'transparent' }}
                        value={formattedValue(selectedDate, 1)}
                        readOnly
                    />
                </Col>
                <Col
                    span={3}
                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: '20px' }}
                >
                    <CalendarOutlined />
                </Col>
            </Row>
        </GrispiInputContainer>
    );
};

export default GrispiDateRangeInput;
